import { useEffect, useRef } from 'react';

/*
 * Starfield + comets canvas, ported from the design's space.js.
 * variant "hero" gets a fixed star count; "body" scales with area and
 * caches the star layer so only comet trails are repainted each frame.
 */
export default function SpaceCanvas({ variant = 'body', className = '' }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const cv = canvasRef.current;
    const ctx = cv.getContext('2d');
    const isHero = variant === 'hero';
    const isStatic = !isHero;
    const reduceMotion = !!(
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
    let raf = null;
    let resizeTimer = null;
    let s = null;

    function initCanvas() {
      const w = cv.offsetWidth;
      const h = cv.offsetHeight;
      if (!w || !h) {
        s = null;
        return;
      }
      cv.width = w;
      cv.height = h;
      const count = isHero ? 180 : Math.max(120, Math.round((w * h) / 14000));
      const colors = [
        [223, 230, 245],
        [255, 255, 255],
        [170, 185, 215],
      ];
      const stars = [];
      for (let i = 0; i < count; i++) {
        stars.push({
          x: Math.random() * w,
          y: Math.random() * h,
          size: Math.random() * 1.6 + 0.3,
          o: Math.random() * 0.7 + 0.15,
          sp: Math.random() * 0.04 + 0.01,
          ph: Math.random() * Math.PI * 2,
          c: colors[Math.floor(Math.random() * colors.length)],
          glow: Math.random() < (isHero ? 0.06 : 0.05),
        });
      }
      s = {
        stars,
        w,
        h,
        drawn: false,
        cache: null,
        comets: [],
        maxComets: Math.min(3, Math.max(1, Math.round(h / 1200))),
        nextComet: null,
        lastT: null,
      };
    }

    function draw() {
      if (!s) return;
      const t = Date.now() * 0.001;
      const { w, h } = s;
      const full = !(isStatic && s.drawn);
      if (full) {
        ctx.clearRect(0, 0, w, h);
        for (let i = 0; i < s.stars.length; i++) {
          const st = s.stars[i];
          const tw = reduceMotion ? 1 : 0.5 + 0.5 * Math.sin(t * st.sp * 12 + st.ph);
          const op = st.o * tw;
          const [r, gc, b] = st.c;
          if (st.glow) {
            const gg = ctx.createRadialGradient(st.x, st.y, 0, st.x, st.y, st.size * 5);
            gg.addColorStop(0, 'rgba(' + r + ',' + gc + ',' + b + ',' + op * 0.3 + ')');
            gg.addColorStop(1, 'rgba(' + r + ',' + gc + ',' + b + ',0)');
            ctx.fillStyle = gg;
            ctx.beginPath();
            ctx.arc(st.x, st.y, st.size * 5, 0, Math.PI * 2);
            ctx.fill();
          }
          ctx.fillStyle = 'rgba(' + r + ',' + gc + ',' + b + ',' + op + ')';
          ctx.beginPath();
          ctx.arc(st.x, st.y, st.size, 0, Math.PI * 2);
          ctx.fill();
        }
        s.drawn = true;
        if (isStatic && !s.cache) {
          s.cache = document.createElement('canvas');
          s.cache.width = w;
          s.cache.height = h;
          s.cache.getContext('2d').drawImage(cv, 0, 0);
        }
      }
      // comets
      if (s.lastT === null) s.lastT = t;
      const dt = Math.min(t - s.lastT, 0.05);
      s.lastT = t;
      if (s.nextComet === null) s.nextComet = t + 1 + Math.random() * 3;
      if (isStatic && s.cache) {
        for (let ci = 0; ci < s.comets.length; ci++) {
          const pr = s.comets[ci].prevRect;
          if (pr) {
            ctx.clearRect(pr.x, pr.y, pr.w, pr.h);
            ctx.drawImage(s.cache, pr.x, pr.y, pr.w, pr.h, pr.x, pr.y, pr.w, pr.h);
          }
        }
      }
      if (t >= s.nextComet && s.comets.length < s.maxComets) {
        const fromLeft = Math.random() < 0.5;
        s.comets.push({
          x: fromLeft ? -40 : w + 40,
          y: Math.random() * Math.max(120, h - 260),
          vx: (fromLeft ? 1 : -1) * (320 + Math.random() * 180),
          vy: 60 + Math.random() * 90,
          life: 3,
          prevRect: null,
        });
        s.nextComet = t + 2.5 + Math.random() * 4.5;
      }
      for (let j = s.comets.length - 1; j >= 0; j--) {
        const c = s.comets[j];
        c.x += c.vx * dt;
        c.y += c.vy * dt;
        c.life -= dt;
        const ang = Math.atan2(c.vy, c.vx);
        const tx = c.x - Math.cos(ang) * 110;
        const ty = c.y - Math.sin(ang) * 110;
        const alpha = Math.max(0, Math.min(1, c.life / 0.8));
        const grad = ctx.createLinearGradient(c.x, c.y, tx, ty);
        grad.addColorStop(0, 'rgba(255,255,255,' + 0.9 * alpha + ')');
        grad.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.strokeStyle = grad;
        ctx.lineWidth = 1.6;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(c.x, c.y);
        ctx.lineTo(tx, ty);
        ctx.stroke();
        ctx.fillStyle = 'rgba(255,255,255,' + 0.95 * alpha + ')';
        ctx.beginPath();
        ctx.arc(c.x, c.y, 1.8, 0, Math.PI * 2);
        ctx.fill();
        let rx = Math.min(c.x, tx) - 26;
        let ry = Math.min(c.y, ty) - 26;
        let rw = Math.abs(c.x - tx) + 52;
        let rh = Math.abs(c.y - ty) + 52;
        if (rx < 0) {
          rw += rx;
          rx = 0;
        }
        if (ry < 0) {
          rh += ry;
          ry = 0;
        }
        rw = Math.min(rw, w - rx);
        rh = Math.min(rh, h - ry);
        c.prevRect = rw > 0 && rh > 0 ? { x: rx, y: ry, w: rw, h: rh } : null;
        if (c.life <= 0 || c.x < -160 || c.x > w + 160 || c.y > h + 60) {
          if (isStatic && s.cache && c.prevRect) {
            const p2 = c.prevRect;
            ctx.clearRect(p2.x, p2.y, p2.w, p2.h);
            ctx.drawImage(s.cache, p2.x, p2.y, p2.w, p2.h, p2.x, p2.y, p2.w, p2.h);
          }
          s.comets.splice(j, 1);
        }
      }
      if (!reduceMotion) raf = requestAnimationFrame(draw);
    }

    function init() {
      initCanvas();
      if (raf) cancelAnimationFrame(raf);
      draw();
    }

    function onResize() {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(init, 200);
    }

    window.addEventListener('resize', onResize);
    init();

    return () => {
      window.removeEventListener('resize', onResize);
      clearTimeout(resizeTimer);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [variant]);

  return <canvas ref={canvasRef} className={className} aria-hidden="true" />;
}
